<template>
  <ContentWrapper class="hero mb-10" fluid>
    <HeroCounter v-if="!mdAndUp && notNoonYet" @now-is-noon="noonHandler" />
    <HeroHint
      v-if="!mdAndUp && !notNoonYet"
      :hint-label="heroHintLabel"
      :hint-text="heroHintText"
      :hint-mobile-text="heroHintMobileText" />
    <div class="position-relative background">
      <ClientOnly>
        <div class="heroBlurdrop" />
        <div
          class="heroBackground"
          :class="{
            bg__gradient_top: (!mdAndUp && mobileBackground === null) || (mdAndUp && bg === null),
          }"
          :style="{
            backgroundImage: `url(https://epd.zentek.dev/assets/${mdAndUp ? bg : mobileBackground}?format=webp&quality=80)`,
          }">
          <div
            class="heroBackgroundGradient"
            :style="
              ' background: linear-gradient(110deg,rgba(' +
              (bgType == 'blue' ? '192, 241, 252, 1' : '87, 51, 228, 1') +
              ') 0%,rgba(' +
              (bgType == 'blue' ? '192, 241, 252, 1' : '87, 51, 228, 1') +
              ') 0%,rgba(' +
              (bgType == 'blue' ? '192, 241, 252, 1' : '87, 51, 228, 1') +
              ') 60%, rgba(255, 255, 255, 0) 80%);'
            " />
        </div>
      </ClientOnly>
      <v-container class="pa-0pt-lg-16 pb-lg-9 position-relative" :fluid="isSafari">
        <v-row class="h-40" justify="start" align-content="start">
          <v-col cols="12" class="pt-9 pt-md-6 pt-xl-10">
            <h1 class="pb-3" :class="{ 'text-white': bgType == 'purple' }">
              {{ title1 }}
              <br />
              {{ title2 }}
            </h1>
            <HeroForm :align-left="true" />
          </v-col>
          <v-col cols="auto">
            <v-row class="d-flex flex-column">
              <v-col
                v-for="claim in claims"
                :key="claim"
                class="font-weight-bold py-2 claims text-left"
                :class="bgType"
                cols="12"
                md="auto">
                <v-icon class="text-primary">custom:checklist</v-icon>
                <span class="pl-4 claim" :class="{ 'text-white': bgType == 'purple' }">
                  {{ claim }}
                </span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row
          v-if="mdAndUp"
          align="start"
          justify="start"
          class="desktop-landing pt-4"
          :class="{ safari: isSafari }">
          <v-col v-if="notNoonYet" cols="auto" class="pb-8 pt-6">
            <HeroCounter @now-is-noon="noonHandler" />
          </v-col>
          <v-col v-else cols="4" class="pt-3 position-relative">
            <HeroHint
              :hint-label="heroHintLabel"
              :hint-text="heroHintText"
              :hint-mobile-text="heroHintMobileText" />
          </v-col>
          <v-col class="pb-0 d-flex justify-end align-items-start">
            <CdeCard width="350" class="pa-5 pb-9">
              <v-row>
                <v-col cols="12" class="font-weight-bold">Sichere Bezahlung mit</v-col>
                <v-col cols="12" class="d-flex flex-row justify-center align-center flex-gap-4">
                  <v-img alt="PayPal" height="20px" src="@/assets/icons/paypal.svg" />
                  <v-img alt="Visa" height="20px" class="mx-4" src="@/assets/icons/visa.svg" />
                  <v-img alt="Klarna" height="20px" src="@/assets/icons/klarna.svg" />
                  <v-img alt="Mastercard" height="30px" src="@/assets/icons/mastercard.svg" />
                </v-col>
              </v-row>
            </CdeCard>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </ContentWrapper>
</template>

<script setup lang="ts">
import { defineProps } from 'vue'
import { useDisplay } from 'vuetify'

const { mdAndUp } = useDisplay()
const { createNewGermanDate } = useDateUtils()
const { isSafari } = useDevice()

const props = defineProps<{
  title1: string
  title2: string | null
  claims: string[]
  bg: string | null
  bgType: string | null
  heroHintLabel: string | null
  heroHintText: string | null
  heroHintMobileText: string | null
  mobileBackground: string | null
}>()

const { title1, title2, mobileBackground, bg, bgType } = props

const notNoonYet = ref(createNewGermanDate().getHours() < 12)

const noonHandler = () => {
  notNoonYet.value = false
}
</script>

<style lang="scss" scoped>
.hero {
  position: relative;
  overflow: visible;
  height: 600px;
  @media (max-width: 960px) {
    height: 820px;
  }
  @media (max-width: 600px) {
    height: 680px;
  }
}
.background {
  height: 600px;
  @media (max-width: 960px) {
    height: 820px;
  }
  @media (max-width: 600px) {
    height: 680px;
  }
}

.heroBlurdrop {
  height: 600px;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 0;
  @media (max-width: 960px) {
    height: 820px;
  }
  @media (max-width: 600px) {
    height: 680px;
  }
}
.heroBackground {
  height: 600px;
  display: block;
  margin: 0;
  padding: 0 auto;
  position: absolute;
  z-index: 0;
  background-size: cover;
  background-position: bottom right;
  width: 100vw;
  @media (max-width: 960px) {
    height: 820px;
    background-size: 60%;
  }
  @media (max-width: 600px) {
    height: 680px;
  }
}

.heroBackgroundGradient {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 0;
  @media (max-width: 960px) {
    height: 780px;
  }
  @media (max-width: 600px) {
    height: 680px;
  }
}

h1 {
  height: 130px;
}
.counter-wrapper {
  height: 60%;
}

:deep(.hint-card) {
  position: relative;
}
</style>
